import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Checkbox,
  ConfirmationResultModal,
  Flex,
  FormField,
  Text,
} from '@percent/lemonade'
import { getAssigneeDisplayName, settleRoleChanges } from '../utils'
import type { EditAssigneeRolesModalProps } from './EditAssigneeRolesModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function EditAssigneeRolesModal({
  domainName,
  domainRoles,
  assignee,
  assignRole,
  unassignRole,
  onChanged,
  onClose,
}: EditAssigneeRolesModalProps) {
  const { t } = useTranslation()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    () => new Set(assignee.roles)
  )
  const [isSaving, setIsSaving] = useState(false)

  const addedRoleIds = [...selectedRoleIds].filter(
    (roleId) => !assignee.roles.includes(roleId)
  )
  const removedRoleIds = assignee.roles.filter(
    (roleId) => !selectedRoleIds.has(roleId)
  )
  const hasChanges = addedRoleIds.length > 0 || removedRoleIds.length > 0
  const values = { name: getAssigneeDisplayName(assignee), domainName }
  const bold = { bold: <Text as="span" fontWeight="semiBold" /> }

  const toggleRole = (roleId: string) =>
    setSelectedRoleIds((previous) => {
      const next = new Set(previous)

      if (next.has(roleId)) {
        next.delete(roleId)
      } else {
        next.add(roleId)
      }

      return next
    })

  const handleConfirm = async () => {
    setIsSaving(true)

    const { anySucceeded, allSucceeded } = await settleRoleChanges([
      ...addedRoleIds.map((roleId) => assignRole(roleId)),
      ...removedRoleIds.map((roleId) => unassignRole(roleId)),
    ])

    setIsSaving(false)

    if (anySucceeded) {
      onChanged()
    }

    setModalState(allSucceeded ? 'success' : 'failure')
  }

  const handleClose = () => {
    if (!isSaving) {
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.editAssigneeRoles.title')}
      primaryButtonText={t('button.saveChanges')}
      loadingText={t('modal.editAssigneeRoles.button.saving')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleConfirm}
      onClose={handleClose}
      onFailureClose={handleClose}
      loading={isSaving}
      disabled={!hasChanges}
      primaryBtnTestId="edit-assignee-roles-submit-button"
      secondaryBtnTestId="edit-assignee-roles-cancel-button"
      success={{
        title: t('modal.editAssigneeRoles.success.title'),
        description: (
          <Trans
            i18nKey="modal.editAssigneeRoles.success.description"
            values={values}
            components={bold}
          />
        ),
        button: t('button.close'),
      }}
      failure={{
        title: t('modal.editAssigneeRoles.error.title'),
        description: (
          <Trans
            i18nKey="modal.editAssigneeRoles.error.description"
            values={values}
            components={bold}
          />
        ),
        button: t('button.close'),
      }}
    >
      <Flex direction="column" gap="md">
        <Text>
          <Trans
            i18nKey="modal.editAssigneeRoles.description"
            values={values}
            components={bold}
          />
        </Text>
        <FormField label={t('modal.editAssigneeRoles.rolesLabel')}>
          <Flex
            direction="column"
            gap="xs"
            role="group"
            aria-label={t('modal.editAssigneeRoles.rolesLabel')}
          >
            {domainRoles.map((role) => (
              <Checkbox
                key={role.id}
                name={role.id}
                variant="default"
                label={role.name}
                helperText={role.description}
                active={selectedRoleIds.has(role.id)}
                disabled={isSaving}
                onChange={() => toggleRole(role.id)}
                dataTestId={`edit-assignee-roles-checkbox-${role.id}`}
              />
            ))}
          </Flex>
        </FormField>
      </Flex>
    </ConfirmationResultModal>
  )
}
